'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useStore } from 'jotai';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { StandardFormFieldDto } from '@app/models/dtos/form';
import { Button } from '@app/shadcn/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader } from '@app/shadcn/components/ui/dialog';
import { toast, toastWhenFree } from '@app/shadcn/components/ui/use-toast';
import { selectForm, setForm } from '@app/store/forms/slice';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useAutosaveStatus } from '@app/store/jotai/autosave-status';
import { builderHydratedFormIdAtom, useBuilderHydration } from '@app/store/jotai/builder-hydration';
import { IFormState, formStateAtom } from '@app/store/jotai/form';
import { builderFieldsAtom, commitBuilderFieldsAtom } from '@app/store/jotai/form-document';
import { adoptSavedLanguageAtom, formLanguageAtom } from '@app/store/jotai/form-language';
import { usePatchV2FormMutation } from '@app/store/redux/form-api';
import { selectWorkspace } from '@app/store/workspaces/slice';
import { FlushResult, SaveOutcome, SaveQueue } from '@app/utils/autosave-queue';
import { DroppedRule, withoutFieldRules } from '@app/utils/field-rule-copies';
import { pruneOptionLogic } from '@app/utils/option-logic-checks';
import { reloadPage } from '@app/utils/reload-page';

/** Quiet time after the last edit before it is saved. */
export const AUTOSAVE_DEBOUNCE_MS = 1000;

/** How the server's answer to a save maps onto the queue. */
/** The field rules a save left out because they no longer fit the form (`droppedFieldRules`). */
export function droppedRulesOf(data: any): DroppedRule[] {
    return Array.isArray(data?.droppedFieldRules) ? data.droppedFieldRules.filter((item: any) => item?.fieldId && item?.ruleId) : [];
}

// Rules already told about in this session ("<form>/<question>/<rule>").
const noticedRules = new Set<string>();

/** Forget which dropped rules were told about (tests). */
export function resetDroppedRuleNotices() {
    noticedRules.clear();
}

/**
 * How many questions lost rules not told about yet (once per rule per
 * session).
 */
export function newlyDroppedQuestions(formId: string, dropped: DroppedRule[]): number {
    const fresh = dropped.filter(({ fieldId, ruleId }) => !noticedRules.has(`${formId}/${fieldId}/${ruleId}`));
    fresh.forEach(({ fieldId, ruleId }) => noticedRules.add(`${formId}/${fieldId}/${ruleId}`));
    return new Set(fresh.map(({ fieldId }) => fieldId)).size;
}

/** The notice for `count` questions that lost a rule. */
export function droppedRulesText(count: number): string {
    return i18n.t('builder:FIELD_RULES.DROPPED', { count });
}

/** A sent body as the server stored it: without the rules it dropped. */
function storedBodyOf(body: string, dropped: DroppedRule[]): string {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed?.fields)) parsed.fields = withoutFieldRules(parsed.fields, dropped);
    return JSON.stringify(parsed);
}

/** Did the server refuse the save because the newer question types are switched off (422 `question_type_disabled`)? */
export function isQuestionTypeDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'question_type_disabled';
}

/** Did the server refuse the save because the address question is switched off (422 `address_question_disabled`)? */
export function isAddressQuestionDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'address_question_disabled';
}

/** Did the server refuse the save because the signature question is switched off (422 `signature_question_disabled`)? */
export function isSignatureQuestionDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'signature_question_disabled';
}

/** Did the server refuse the save because layout containers are switched off (422 `layout_containers_disabled`)? */
export function isLayoutContainersDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'layout_containers_disabled';
}

/** Did the server refuse the save because a page's layout containers are not valid (422 `invalid_layout`)? */
export function isInvalidLayout(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'invalid_layout';
}

/** Did the server refuse the save because field rules are switched off (422 `field_rules_disabled`)? */
export function isFieldRulesDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'field_rules_disabled';
}

/** Did the server refuse the save because choice logic is switched off (422 `choice_logic_disabled`)? */
export function isChoiceLogicDisabled(response: any): boolean {
    return response?.error?.status === 422 && response.error.data?.code === 'choice_logic_disabled';
}

/** A piece of option logic a save repaired (dropped): where, and why. */
export interface RepairedOptionLogic {
    fieldId?: string | null;
    optionId?: string | null;
    message?: string | null;
}

/** The option logic a save repaired (`droppedOptionLogic`). */
export function droppedOptionLogicOf(data: any): RepairedOptionLogic[] {
    return Array.isArray(data?.droppedOptionLogic) ? data.droppedOptionLogic.filter((item: any) => item?.fieldId) : [];
}

// Repairs already told about in this session ("<form>/<question>/<option>/<message>").
const noticedOptionLogic = new Set<string>();

/** Forget which repairs were told about (tests). */
export function resetRepairedOptionLogicNotices() {
    noticedOptionLogic.clear();
}

/** How many repairs were not told about yet (once each per session). */
export function newlyRepairedOptionLogic(formId: string, repaired: RepairedOptionLogic[]): number {
    const fresh = repaired.filter(({ fieldId, optionId, message }) => !noticedOptionLogic.has(`${formId}/${fieldId}/${optionId ?? ''}/${message ?? ''}`));
    fresh.forEach(({ fieldId, optionId, message }) => noticedOptionLogic.add(`${formId}/${fieldId}/${optionId ?? ''}/${message ?? ''}`));
    return fresh.length;
}

/** The notice for `count` repairs. */
export function repairedOptionLogicText(count: number): string {
    return i18n.t('builder:OPTION_LOGIC.DROPPED', { count });
}

/** A sent body as the server stored it: with its option logic repaired the way the server repairs it. */
function bodyWithRepairedOptionLogic(body: string): string {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed?.fields)) pruneOptionLogic(parsed.fields);
    return JSON.stringify(parsed);
}

export function saveOutcomeOf(response: any): SaveOutcome {
    if (response?.data) return { kind: 'saved', version: response.data.updatedAt ?? null };
    const status = response?.error?.status;
    if (status === 409) return { kind: 'conflict' };
    if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 502 || status === 503 || status === 504) return { kind: 'retryable' };
    return { kind: 'failed' };
}

// The open editor's queue, for the publish button and the AI panel (siblings
// in the tree).
let activeAutosave: { formId: string; flush: () => Promise<FlushResult>; adoptVersion: (version: string | null | undefined) => void } | null = null;

/**
 * The editor stored a newer version itself (an AI edit is saved by the
 * server): later saves build on it instead of reading it as a change made
 * elsewhere. Only for versions whose content the editor now shows.
 */
export function adoptAutosaveVersion(version: string | null | undefined) {
    activeAutosave?.adoptVersion(version);
}

/**
 * Save everything edited so far right away (skipping the debounce) and wait
 * for it. Resolves 'saved' when no editor is open.
 */
export async function flushAutosave(timeoutMs = 20000): Promise<FlushResult | 'timeout'> {
    if (!activeAutosave) return 'saved';
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => {
        timer = setTimeout(() => resolve('timeout'), timeoutMs);
    });
    try {
        return await Promise.race([activeAutosave.flush(), timeout]);
    } finally {
        clearTimeout(timer);
    }
}

/** What autosave reads: the form-level state, the builder's pages and the form's language. */
interface SavedParts {
    formState: IFormState;
    fields: StandardFormFieldDto[];
    language: string | null;
}

const currentParts = (store: ReturnType<typeof useStore>): SavedParts => ({ formState: store.get(formStateAtom), fields: store.get(builderFieldsAtom), language: store.get(formLanguageAtom) });
const sameParts = (a: SavedParts, b: SavedParts) => a.formState === b.formState && a.fields === b.fields && a.language === b.language;

/**
 * What autosave sends: the form-level state as the document holds it (a
 * rename titles the welcome page there, see setFormTitle) with the builder's
 * pages, and the form's own language as its one setting (null clears it; the
 * server keeps the form's other settings, and the workspace's are saved apart).
 */
function savedFormOf({ formState, fields, language }: SavedParts) {
    return {
        ...formState,
        fields,
        settings: { language }
    };
}

export default function AutoSaveForm({ formId }: { formId: string }) {
    const { t } = useTranslation('builder');
    // Autosave follows the form through store subscriptions, not renders:
    // typing does not re-render it.
    const store = useStore();
    const workspace = useAppSelector(selectWorkspace);
    const { hydratedFormId } = useBuilderHydration();
    const hydrated = hydratedFormId === formId;

    const dispatch = useAppDispatch();
    const form = useAppSelector(selectForm);
    const [patchV2Form] = usePatchV2FormMutation();
    const { setAutosaveStatus } = useAutosaveStatus();
    const [conflict, setConflict] = useState(false);

    // What the save request needs, kept current for the queue's sender.
    const contextRef = useRef({ workspaceId: workspace?.id as string | undefined, settings: form?.settings, patchV2Form, dispatch });
    useEffect(() => {
        contextRef.current = { workspaceId: workspace?.id, settings: form?.settings, patchV2Form, dispatch };
    });

    // On while the builder applies what the server stored (not an edit).
    const [quiet] = useState(() => ({ on: false }));
    // Notices still waiting for an Undo toast to close: cancelled when the editor goes away.
    const [waitingNotices] = useState(() => new Set<() => void>());
    useEffect(
        () => () => {
            waitingNotices.forEach((cancel) => cancel());
            waitingNotices.clear();
        },
        [waitingNotices]
    );
    const [queue] = useState(
        () =>
            new SaveQueue(
                {
                    send: async (body, version) => {
                        const { workspaceId, patchV2Form: patch } = contextRef.current;
                        const formData = new FormData();
                        formData.append('form_body', body);
                        // The server refuses (409) a save based on an older draft.
                        if (version) formData.append('expected_updated_at', version);
                        const response: any = await patch({ formId, workspaceId, body: formData });
                        const outcome = saveOutcomeOf(response);
                        // Rules the server will not take (they are switched off there): say so plainly, once per refusal.
                        if (isFieldRulesDisabled(response)) toast({ description: i18n.t('builder:FIELD_RULES.DISABLED') });
                        if (isChoiceLogicDisabled(response)) toast({ description: i18n.t('builder:OPTION_LOGIC.DISABLED') });
                        // The same for the newer question types (NEW_QUESTION_TYPES_ENABLED is off there).
                        if (isQuestionTypeDisabled(response)) toast({ description: i18n.t('builder:NEW_TYPES.SAVE_REFUSED') });
                        if (isSignatureQuestionDisabled(response)) toast({ description: i18n.t('builder:NEW_TYPES.SIGNATURE_SAVE_REFUSED') });
                        if (isAddressQuestionDisabled(response)) toast({ description: i18n.t('builder:NEW_TYPES.ADDRESS_SAVE_REFUSED') });
                        if (isLayoutContainersDisabled(response)) toast({ description: i18n.t('builder:LAYOUT.SAVE_REFUSED') });
                        if (isInvalidLayout(response)) toast({ description: i18n.t('builder:LAYOUT.SAVE_INVALID') });
                        if (outcome.kind === 'saved') {
                            contextRef.current.dispatch(setForm({ ...response.data, settings: contextRef.current.settings }));
                            // The language the server stored: it keeps its own
                            // for a tag that cannot be set (after an undo back to
                            // an imported "en-GB", say), and the builder shows that.
                            const saved = response.data?.settings;
                            if (saved && typeof saved === 'object' && 'language' in saved) {
                                store.set(adoptSavedLanguageAtom, { sent: JSON.parse(body)?.settings?.language ?? null, saved: saved.language ?? null });
                            }
                            const dropped = droppedRulesOf(response.data);
                            const repaired = droppedOptionLogicOf(response.data);
                            if (repaired.length) {
                                // The server repaired these (a condition or filter that no longer fits
                                // the form): the builder repairs its own copy the same way (no undo
                                // step, not an edit) and so do the saves waiting, and says so once.
                                const fields = store.get(builderFieldsAtom);
                                const copy = JSON.parse(JSON.stringify(fields));
                                pruneOptionLogic(copy);
                                if (JSON.stringify(copy) !== JSON.stringify(fields)) {
                                    quiet.on = true;
                                    store.set(commitBuilderFieldsAtom, copy, { undoable: false });
                                    quiet.on = false;
                                }
                                const fresh = newlyRepairedOptionLogic(formId, repaired);
                                if (fresh) waitingNotices.add(toastWhenFree({}, { key: `option-logic:${formId}`, count: fresh, describe: repairedOptionLogicText }));
                            }
                            if (repaired.length && !dropped.length) {
                                return { ...outcome, savedBody: bodyWithRepairedOptionLogic(body), rewritePending: (waiting: string) => bodyWithRepairedOptionLogic(waiting) } as SaveOutcome;
                            }
                            if (dropped.length) {
                                // The server left these rules out: the builder lets go of
                                // them too (no undo step, not an edit), and neither the
                                // save waiting in the queue (rewritePending) nor later
                                // ones send them again.
                                const fields = store.get(builderFieldsAtom);
                                const pruned = withoutFieldRules(fields, dropped);
                                if (pruned !== fields) {
                                    quiet.on = true;
                                    store.set(commitBuilderFieldsAtom, pruned, { undoable: false });
                                    quiet.on = false;
                                }
                                const questions = newlyDroppedQuestions(formId, dropped);
                                // never in place of an open Undo toast; two waiting notices become one
                                if (questions) {
                                    const cancel = toastWhenFree({}, { key: `field-rules:${formId}`, count: questions, describe: droppedRulesText });
                                    waitingNotices.add(cancel);
                                }
                                const rewrite = (text: string) => (repaired.length ? bodyWithRepairedOptionLogic(storedBodyOf(text, dropped)) : storedBodyOf(text, dropped));
                                return { ...outcome, savedBody: rewrite(body), rewritePending: rewrite } as SaveOutcome;
                            }
                        }
                        return outcome;
                    },
                    onState: (state, { savedAt }) => {
                        setAutosaveStatus(state === 'saved' ? { state, savedAt } : { state });
                        if (state === 'conflict') setConflict(true);
                    }
                },
                form?.updatedAt ?? null
            )
    );

    const latestRef = useRef(savedFormOf(currentParts(store)));
    const baselineTakenRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const allowLeaveRef = useRef(false);

    const enqueueLatest = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        if (!baselineTakenRef.current || !contextRef.current.workspaceId) return;
        queue.enqueue(JSON.stringify(latestRef.current));
    };

    /** Does the builder hold this form now (loaded into it, and not let go of yet)? */
    const holdsThisForm = () => store.get(builderHydratedFormIdAtom) === formId;

    // The form as it is now: the first state once hydrated is what the
    // server has; each later change is saved AUTOSAVE_DEBOUNCE_MS after the
    // last one. Once that first state is taken, only states of this form,
    // while the builder holds it, count: the editor page resets the builder
    // when it closes (before this component's own cleanup flushes), and that
    // empty form must never be what is saved.
    const takeState = (parts: SavedParts) => {
        if (baselineTakenRef.current && !holdsThisForm()) return;
        const combinedFormState = savedFormOf(parts);
        latestRef.current = combinedFormState;
        // Hydrated as of the latest render: the render that hydrated takes the baseline.
        if (!hydratedRef.current) return;
        // The first state after loading is what the server has.
        if (!baselineTakenRef.current) {
            baselineTakenRef.current = true;
            queue.setBaseline(JSON.stringify(combinedFormState));
            return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(enqueueLatest, AUTOSAVE_DEBOUNCE_MS);
    };
    const takeStateRef = useRef(takeState);
    const hydratedRef = useRef(hydrated);
    useLayoutEffect(() => {
        takeStateRef.current = takeState;
        hydratedRef.current = hydrated;
    });
    // What this render saw: hydrating takes it, as the render that hydrated showed it.
    const rendered: SavedParts = currentParts(store);

    useEffect(() => {
        takeState(rendered);
        // Changed since that render (by another mount effect): that is an edit.
        const now = currentParts(store);
        if (baselineTakenRef.current && !sameParts(now, rendered)) takeState(now);
    }, [hydrated]);

    useEffect(() => {
        // Each change of the pages or the form-level state, without re-rendering.
        let last = currentParts(store);
        const changed = () => {
            // The builder no longer holds this form (the editor is closing and reset it): not an edit.
            if (baselineTakenRef.current && store.get(builderHydratedFormIdAtom) !== formId) return;
            const now = currentParts(store);
            if (sameParts(now, last)) return;
            last = now;
            // The builder letting go of rules a save dropped: what the server has, not an edit.
            if (quiet.on) {
                latestRef.current = savedFormOf(now);
                return;
            }
            // Before hydrating (and taking the server's state) a change only updates what is latest.
            if (!baselineTakenRef.current) latestRef.current = savedFormOf(now);
            else takeStateRef.current(now);
        };
        const unsubscribe = [store.sub(builderFieldsAtom, changed), store.sub(formStateAtom, changed), store.sub(formLanguageAtom, changed)];
        return () => unsubscribe.forEach((stop) => stop());
    }, [store, formId]);

    // An edit made while the workspace was still loading saves once it is.
    useEffect(() => {
        if (workspace?.id && baselineTakenRef.current && !timerRef.current) enqueueLatest();
    }, [workspace?.id]);

    const hasUnsaved = () => {
        if (!baselineTakenRef.current) return false;
        return timerRef.current !== null || queue.hasUnsaved() || JSON.stringify(latestRef.current) !== queue.knownBody();
    };

    useEffect(() => {
        setAutosaveStatus({ state: 'idle' });
        activeAutosave = {
            formId,
            flush: () => {
                enqueueLatest();
                return queue.flush();
            },
            adoptVersion: (version) => queue.adoptVersion(version)
        };
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (allowLeaveRef.current || !hasUnsaved()) return;
            event.preventDefault();
            // Older browsers show the prompt only with returnValue set.
            event.returnValue = '';
        };
        const onOnline = () => queue.retryNow();
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('online', onOnline);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('online', onOnline);
            if (activeAutosave?.formId === formId) activeAutosave = null;
            // Leaving the editor inside the app: save what the debounce held,
            // the latest state of this form taken while the builder held it
            // (see takeState), never the reset that closing the editor makes.
            if (timerRef.current) enqueueLatest();
        };
    }, [formId]);

    const reloadLatest = () => {
        allowLeaveRef.current = true;
        reloadPage();
    };

    return (
        <Dialog open={conflict} onOpenChange={(open) => !open && setConflict(false)}>
            <DialogContent className="max-w-md" title={t('AUTOSAVE.CONFLICT_TITLE')}>
                <DialogHeader>
                    <div aria-hidden="true" className="text-lg leading-none font-semibold tracking-tight">
                        {t('AUTOSAVE.CONFLICT_TITLE')}
                    </div>
                    <DialogDescription>{t('AUTOSAVE.CONFLICT_BODY')}</DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setConflict(false)}>
                        {t('AUTOSAVE.NOT_NOW')}
                    </Button>
                    <Button onClick={reloadLatest} className="text-white" data-testid="reload-latest">
                        {t('AUTOSAVE.RELOAD')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
